"use client";

import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Loader2,
  ArrowUpCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { getApiUrl } from "@/lib/api-config";
import { getAuthToken, getAuthTokenWithRefresh } from "@/lib/auth";

interface VersionInfo {
  version: string;
  release_notes: string | null;
  release_description: string | null;
  is_latest: boolean | null;
  released_at: string | null;
  latest_available: string | null;
  update_available: boolean;
}

interface Release {
  version: string;
  release_notes: string | null;
  release_description: string | null;
  release_summary_md: string | null;
  is_latest: boolean;
  is_prerelease: boolean;
  released_at: string;
}

interface VersionInfoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function VersionInfoDialog({
  open,
  onOpenChange,
}: VersionInfoDialogProps) {
  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);
  const [releases, setReleases] = useState<Release[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedVersion, setExpandedVersion] = useState<string | null>(null);

  const getHeaders = useCallback(async () => {
    let token = getAuthToken();
    if (!token) {
      token = await getAuthTokenWithRefresh();
    }
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    return headers;
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const headers = await getHeaders();
      const apiUrl = getApiUrl();

      // Fetch current version and release history in parallel
      const [versionRes, releasesRes] = await Promise.all([
        fetch(`${apiUrl}/v1/mstrohub/version`, { headers }),
        fetch(`${apiUrl}/v1/mstrohub/releases`, { headers }).catch(() => null),
      ]);

      if (!versionRes.ok) {
        throw new Error(`Failed to fetch version info (${versionRes.status})`);
      }

      const versionData: VersionInfo = await versionRes.json();
      setVersionInfo(versionData);

      // Parse release history if available
      if (releasesRes && releasesRes.ok) {
        const releasesData = await releasesRes.json();
        setReleases(releasesData.releases || []);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load version info",
      );
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open, fetchData]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDateShort = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const toggleExpanded = (version: string) => {
    setExpandedVersion(expandedVersion === version ? null : version);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] gap-0 overflow-hidden border-white/[0.06] bg-[#000000]">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="text-base font-medium text-white/90">
            Release Notes
          </DialogTitle>
          <DialogDescription className="text-sm text-white/40">
            Version history and update details
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 max-h-[calc(85vh-100px)]">
          <div className="px-6 pb-6">
            {loading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-white/60" />
              </div>
            )}

            {error && (
              <div className="rounded-lg border border-red-500/20 bg-red-500/5 px-4 py-3">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            {!loading && !error && versionInfo && (
              <div className="space-y-6">
                {/* Current version header */}
                <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-xl font-semibold tracking-tight text-white">
                        {versionInfo.version}
                      </span>
                      {versionInfo.is_latest === true && (
                        <Badge
                          variant="outline"
                          className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400 text-xs"
                        >
                          <CheckCircle2 className="mr-1 h-3 w-3" />
                          Latest
                        </Badge>
                      )}
                      {versionInfo.update_available && (
                        <Badge
                          variant="outline"
                          className="border-amber-500/30 bg-amber-500/10 text-amber-400 text-xs"
                        >
                          <ArrowUpCircle className="mr-1 h-3 w-3" />
                          {versionInfo.latest_available} Available
                        </Badge>
                      )}
                    </div>
                    {versionInfo.released_at && (
                      <span className="text-xs text-white/60">
                        {formatDate(versionInfo.released_at)}
                      </span>
                    )}
                  </div>
                  {versionInfo.release_description && (
                    <p className="mt-2 text-sm leading-relaxed text-white/50">
                      {versionInfo.release_description}
                    </p>
                  )}
                </div>

                {/* Release history */}
                {releases.length > 0 && (
                  <div className="space-y-1">
                    <h4 className="text-xs font-medium uppercase tracking-wider text-white/60 mb-3">
                      Release History
                    </h4>

                    <div className="space-y-0">
                      {releases.map((release, index) => (
                        <div key={release.version}>
                          <div className="group py-3">
                            {/* Version header row */}
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-semibold text-white/80">
                                    Version {release.version}
                                  </span>
                                  <span className="text-xs text-white/40">
                                    {formatDateShort(release.released_at)}
                                  </span>
                                  {release.is_latest && (
                                    <Badge
                                      variant="outline"
                                      className="border-emerald-500/20 bg-emerald-500/5 text-emerald-400/70 text-[10px] px-1.5 py-0"
                                    >
                                      Latest
                                    </Badge>
                                  )}
                                  {release.is_prerelease && (
                                    <Badge
                                      variant="outline"
                                      className="border-blue-500/20 bg-blue-500/5 text-blue-400/70 text-[10px] px-1.5 py-0"
                                    >
                                      Pre-release
                                    </Badge>
                                  )}
                                </div>

                                {/* Description */}
                                {release.release_description && (
                                  <p className="mt-1 text-xs leading-relaxed text-white/40">
                                    {release.release_description}
                                  </p>
                                )}

                                {/* Expandable details toggle */}
                                {release.release_notes && (
                                  <button
                                    onClick={() =>
                                      toggleExpanded(release.version)
                                    }
                                    className="mt-1.5 flex items-center gap-1 text-[13px] text-white/40 hover:text-white/50 transition-colors"
                                  >
                                    {expandedVersion === release.version ? (
                                      <>
                                        <ChevronUp className="h-3 w-3" />
                                        Hide details
                                      </>
                                    ) : (
                                      <>
                                        <ChevronDown className="h-3 w-3" />
                                        See update details
                                      </>
                                    )}
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Expanded release notes */}
                            {expandedVersion === release.version &&
                              release.release_notes && (
                                <div className="mt-3 rounded-md border border-white/[0.04] bg-white/[0.015] p-3">
                                  <pre className="text-[13px] leading-relaxed text-white/50 whitespace-pre-wrap font-sans">
                                    {release.release_notes
                                      .replace(
                                        /^## mHive [\d.]+\n+### Docker Images[\s\S]*?```\n+### Changelog\n+/,
                                        "",
                                      )
                                      .replace(
                                        /\n+### Installation[\s\S]*$/,
                                        "",
                                      )
                                      .trim()}
                                  </pre>
                                </div>
                              )}
                          </div>

                          {index < releases.length - 1 && (
                            <Separator className="bg-white/[0.04]" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
